/**
 * Base builder user types - extracted to break circular dependencies between
 * builderUsersResponses.ts, permissionsResponses.ts, and usersResponses.ts
 */
import type { BuilderProduct } from "api-types/builders/builderTypes";
import type { FeatureName, FeatureSet } from "common/constants/features";

export type ClientBuilderUser = {
  id: string;
  userId: string;
  builderId: string;
  companyName: string | null;
  title?: string | null;
  externalId?: string | null;
  featureFlags?: FeatureSet | null;
  createdAt: Date;
  updatedAt: Date;
};

export type UserManagementUIVisibilitySettings = {
  showInUserManagementUI?: boolean;
  gateBehindFeatureFlag?: FeatureName;
  gateBehindProduct?: BuilderProduct;
};

type ClientBuilderUserPermissionDefinition = {
  permission: string;
  canBeResourceScoped?: boolean;
} & UserManagementUIVisibilitySettings;

type ClientBuilderUserPermissionEntry = string | ClientBuilderUserPermissionDefinition;

type ClientBuilderUserPermissionGroupDefinition = {
  group: string;
  permissions: readonly ClientBuilderUserPermissionEntry[];
} & UserManagementUIVisibilitySettings;

function getPermissionEntryCode(permission: ClientBuilderUserPermissionEntry): string {
  return typeof permission === "string" ? permission : permission.permission;
}

function permissionEntrySupportsResourceScopedFallback(permission: ClientBuilderUserPermissionEntry): boolean {
  if (typeof permission === "string") {
    return false;
  }

  return permission.canBeResourceScoped === true;
}

/**
 * Permission groups - the canonical source of truth for permission organization.
 * Used by both the API types and the UI.
 */
export const clientBuilderUserPermissionGroups = [
  {
    group: "Internal Permissions", // Special permissions group
    showInUserManagementUI: false,
    permissions: ["VIEW_INTERNAL_IDS", "ARCHIVE_COMMUNITIES", "DEBUG_COMMUNITY_MAP"],
  },
  {
    group: "API & Client Apps",
    gateBehindProduct: "api",
    permissions: ["VIEW_CLIENT_APPS", "EDIT_CLIENT_APPS"],
  },
  {
    group: "Builder Settings & Admin",
    permissions: [
      "VIEW_PRICING_VISIBILITY",
      "EDIT_PRICING_VISIBILITY",
      "VIEW_BUILDER_INFO",
      "EDIT_BUILDER_INFO",
      "VIEW_BROCHURE_SETTINGS",
      "EDIT_BROCHURE_SETTINGS",
      "VIEW_SALES_SETTINGS",
      "EDIT_SALES_SETTINGS",
      "VIEW_STAFF_PERMISSIONS",
      "EDIT_STAFF_PERMISSIONS",
      "INVITE_NEW_USERS_TO_BUILDER",
      "VIEW_LOCATION_SETTINGS",
      "EDIT_LOCATION_SETTINGS",
    ],
  },
  {
    group: "Credits",
    permissions: ["VIEW_CREDITS", "EDIT_CREDITS"],
  },
  {
    group: "Customers",
    permissions: ["EDIT_CUSTOMER_INFO"],
  },
  {
    group: "Estimate",
    gateBehindProduct: "procure",
    permissions: [
      "VIEW_PROCURE",
      "EDIT_PROCURE",
      "ADD_ITEM_CODE",
      "MODIFY_ITEM_CODE",
      "DELETE_ITEM_CODE",
      "ADD_COST_CODE",
      "MODIFY_COST_CODE",
      "DELETE_COST_CODE",
      "ADD_MEASURE_GROUP",
      "MODIFY_MEASURE_GROUP",
      "DELETE_MEASURE_GROUP",
      "ADD_RESOURCE_MAPPING",
      "MODIFY_RESOURCE_MAPPING",
      "DELETE_RESOURCE_MAPPING",
    ],
  },
  {
    group: "Auto Renders",
    gateBehindFeatureFlag: "autoRenders",
    gateBehindProduct: "autoRenders",
    permissions: ["VIEW_AUTO_RENDERS", "CREATE_AUTO_RENDERS"],
  },
  {
    group: "Home Plans",
    permissions: [
      "VIEW_HOME_PLANS",
      "CREATE_HOME_PLANS",
      "EDIT_HOME_PLANS",
      "DELETE_HOME_PLANS",
      "SET_CATEGORY_ON_HOME_PLANS",
      "CHANGE_HOME_PLAN_STATUS",
      "ASSIGN_SALES_AGENT_TO_HOME_PLAN",
      "EDIT_HOME_PLAN_DISCOUNT",
      "EDIT_HOME_PLAN_TOTAL_PRICE_OVERRIDE",
      "MOVE_HOME_PLAN",
      "MOVE_CUSTOMER_PLAN",
      "EDIT_HOME_PLAN_LOT",
      "DETACH_HOME_PLAN",
    ],
  },
  {
    group: "Integrations",
    permissions: ["VIEW_INTEGRATIONS", "EDIT_INTEGRATIONS", "TRIGGER_INTEGRATION_JOBS"],
  },
  {
    group: "Locations",
    permissions: [
      { permission: "VIEW_COMMUNITIES", canBeResourceScoped: true },
      "EDIT_COMMUNITIES",
      "ASSIGN_SALES_AGENT_TO_COMMUNITY",
      "VIEW_CHILD_LOCATIONS",
    ],
  },
  {
    group: "Location Plans",
    permissions: [{ permission: "VIEW_COMMUNITY_PLANS", canBeResourceScoped: true }, "EDIT_COMMUNITY_PLANS"],
  },
  {
    group: "Lot-Specific Location Plans",
    permissions: ["VIEW_LOT_COMMUNITY_PLANS", "EDIT_LOT_COMMUNITY_PLANS", "ASSIGN_COMMUNITY_PLAN_TO_LOT"],
  },
  {
    group: "Lots",
    permissions: ["VIEW_LOTS", "EDIT_LOTS", "EDIT_LOT_DISCOUNT"],
  },
  {
    group: "Maps",
    permissions: ["VIEW_COMMUNITY_MAP", "EDIT_COMMUNITY_MAP"],
  },
  {
    group: "Master Plans",
    permissions: ["VIEW_MASTER_PLANS", "EDIT_MASTER_PLANS"],
  },
  {
    group: "Option Catalog",
    gateBehindFeatureFlag: "nextGenOptions",
    permissions: [
      "VIEW_OPTION_CATALOG",
      "EDIT_OPTION_CATALOG",
      "PROMOTE_OPTION_TO_CATALOG",
      "PROMOTE_BULK_OPTIONS_TO_CATALOG",
    ],
  },
  {
    group: "Plan Documents & Data",
    permissions: [
      "DOWNLOAD_PLAN_SETS",
      "DOWNLOAD_PLAN_SETS_IF_SALES_STAFF",
      "DOWNLOAD_BUILT_IN_PLAN_SETS",
      "DOWNLOAD_HOME_PLAN_DATA_CSV",
    ],
  },
  {
    group: "Sales & Marketing",
    permissions: [
      "VIEW_SALES_CATEGORIES",
      "EDIT_SALES_CATEGORIES",
      "VIEW_OPERATIONS_MODE",
      "ASSIGNABLE_AS_SALES_AGENT",
      "ASSIGNABLE_AS_SALES_MANAGER",
      "ASSIGNABLE_AS_SALES_TEAM_LEAD",
      "ALLOWED_AS_SALES_STAFF",
    ],
  },
  {
    group: "Showroom",
    gateBehindProduct: "showroom",
    permissions: ["EDIT_SHOWROOM_INTRO"],
  },
  {
    group: "Studio Files",
    gateBehindProduct: "studio",
    permissions: ["VIEW_STUDIO_FILES", "EDIT_STUDIO_FILES", "ASSIGN_USERS_TO_STUDIO_FILES"],
  },
] as const satisfies readonly ClientBuilderUserPermissionGroupDefinition[];

type ClientBuilderUserPermissionEntryValue = (typeof clientBuilderUserPermissionGroups)[number]["permissions"][number];

type PermissionFromEntry<TPermissionEntry extends ClientBuilderUserPermissionEntryValue> = TPermissionEntry extends {
  permission: infer TPermission extends string;
}
  ? TPermission
  : TPermissionEntry extends string
  ? TPermissionEntry
  : never;

export type ClientBuilderUserPermission = PermissionFromEntry<ClientBuilderUserPermissionEntryValue>;

export type ClientBuilderUserPermissionGroup = {
  group: string;
  permissions: ClientBuilderUserPermission[];
} & UserManagementUIVisibilitySettings;

// Derive flat array from groups for backwards compatibility with zod schemas and type derivation
export const clientBuilderUserPermissions = clientBuilderUserPermissionGroups.flatMap((g) =>
  g.permissions.map((permission) => getPermissionEntryCode(permission) as ClientBuilderUserPermission),
);

export const clientBuilderUserPermissionsWithResourceScopedFallback = clientBuilderUserPermissionGroups.flatMap((g) =>
  g.permissions
    .filter((permission) => permissionEntrySupportsResourceScopedFallback(permission))
    .map((permission) => getPermissionEntryCode(permission) as ClientBuilderUserPermission),
);

export type ResourceScopedPermissionResourceType = "communities" | "community_plans";
export type ResourceScopedPermissionKind = "view" | "edit";

export function getResourceScopedPermission(args: {
  resourceType: ResourceScopedPermissionResourceType;
  kind: ResourceScopedPermissionKind;
}): ClientBuilderUserPermission {
  if (args.resourceType === "communities") {
    return args.kind === "view" ? "VIEW_COMMUNITIES" : "EDIT_COMMUNITIES";
  }

  return args.kind === "view" ? "VIEW_COMMUNITY_PLANS" : "EDIT_COMMUNITY_PLANS";
}

export function getResourceScopedViewPermission(
  resourceType: ResourceScopedPermissionResourceType,
): ClientBuilderUserPermission {
  return getResourceScopedPermission({ resourceType, kind: "view" });
}

export const clientBuilderUserPermissionValues = ["ALLOW", "DENY", "DEFER"] as const;
type ClientBuilderUserPermissionValue = (typeof clientBuilderUserPermissionValues)[number];
export type ClientBuilderUserPermissions = Record<ClientBuilderUserPermission, ClientBuilderUserPermissionValue>;

/**
 * Permission display names - for permissions that need a custom display name
 * different from the auto-generated one (e.g., "VIEW_PROCURE" -> "View Estimate")
 */
export const permissionDisplayNames: Partial<Record<ClientBuilderUserPermission, string>> = {
  VIEW_PROCURE: "View Estimate",
  EDIT_PROCURE: "Edit Estimate",
};
